import { useState, useMemo } from "react";
import Sidebar from "../../sidebar/sidebar";
import Header from "../../header/header";
import { useKpiData } from "../../DataManagment/Quantative/hooks/useKpiData";
import TagFilterBar from "../../Settings/Components/TagFilterBar";
import { questionMatchesSteps, parseTags } from "../../Settings/Components/TagFilterUtils";
import { useKpiRowData } from "../../DataManagment/Quantative/component/KpiRow/useKpiRowData";
import KpiRowOverview from "../../DataManagment/Quantative/component/KpiRow/KpiRowOverview";
import { formatNumber } from "../../../utils/numberFormat";
import { ScopeDashboard } from "./components/ScopeDashboard";
import { ViewToggle } from "./components/ViewToggle";
import { downloadFyViewExcel, downloadBtnStyle } from "./components/scopeExcel";
import useScopeSummary from "../common/hooks/useEmission";

/** Route prop → API scope key + locked tag value. */
const SCOPE_KEY = { SCOPE1: "scope 1", SCOPE2: "scope 2", SCOPE3: "scope 3" };

const norm = (v) => String(v || "").toLowerCase().replace(/\s+/g, "");
const isScopeVal = (v) => ["scope1", "scope2", "scope3"].includes(norm(v));

/** Human FY label. */
const fyLabelOf = (fy) => {
  const v = fy?.financial_year_value || fy?.name;
  return v ? `FY ${v}` : "";
};

/**
 * Standalone carbon-footprint scope (FY-view) page. Reuses the shared data hook
 * (`useKpiData`) and chrome (Sidebar/Header) by import, but renders its own
 * minimal list — KPI + Emission only — so the shared DataManagement components
 * are never modified.
 * @param {string} props.scopeType - SCOPE1 | SCOPE2 | SCOPE3
 * @param {string} props.title - page heading
 * @param {string} props.viewMode
 * @param {(mode:string)=>void} props.onViewChange
 * @param {string} props.routePath
 */
const ScopeListPage = ({ scopeType, title, viewMode, onViewChange, routePath }) => {
  const apiKey = SCOPE_KEY[scopeType];
  const scopeVal = apiKey; // "scope 1"

  const [locationId, setLocationId] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const locationIds = locationId ? [Number(locationId)] : [];
  const answerFilters = useMemo(
    () => ({ location_ids: locationIds, from_date: "", to_date: "", auditor_type: "" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationId],
  );

  const {
    kpiList,
    isLoading,
    financialYears,
    locations,
    selectedFY,
    setSelectedFY,
    answeredQids,
  } = useKpiData({
    answerFilters,
    emissionMode: true,
  });

  // ── Scope filter (this page is fixed to one scope) ──
  const scopeList = useMemo(() => {
    const target = norm(scopeVal);
    return (kpiList || []).filter((item) => {
      const combos = parseTags(item.rawTags);
      if (!Array.isArray(combos) || combos.length === 0) return false;
      return combos.some((combo) =>
        Array.isArray(combo) &&
        combo.some((pair) => {
          const isScopePair = pair?.key?.toLowerCase() === "scope" || isScopeVal(pair?.value);
          return isScopePair && norm(pair?.value) === target;
        }),
      );
    });
  }, [kpiList, scopeVal]);

  // ── Tag questions + locked scope for the interactive category filter ──
  const tagQuestions = useMemo(
    () => scopeList.map((item) => ({ ...item, tags: item.rawTags, questionId: item.id, kpiId: item.id })),
    [scopeList],
  );
  // Lock to the scope tag's EXACT key/value as stored in the data (e.g. "Scope1"),
  // not our normalised "scope 1" — otherwise TagFilterBar won't see it as selected
  // and re-offers Scope in the cascade.
  const lockedSteps = useMemo(() => {
    const target = norm(scopeVal);
    for (const item of scopeList) {
      for (const combo of parseTags(item.rawTags)) {
        if (!Array.isArray(combo)) continue;
        const pair = combo.find((p) => p?.key?.toLowerCase() === "scope" && norm(p?.value) === target);
        if (pair) return [{ key: pair.key, values: new Set([pair.value]) }];
      }
    }
    return [{ key: "scope", values: new Set([scopeVal]) }];
  }, [scopeList, scopeVal]);

  // ── Apply category tag filters + emission-answered filter ──
  const filtered = useMemo(() => {
    const activeSteps = [...lockedSteps, ...filterSteps].filter((s) => s.values?.size);
    return scopeList.filter((item) => {
      if (!answeredQids?.has(String(item.id))) return false;
      return questionMatchesSteps({ ...item, tags: item.rawTags, questionId: item.id }, activeSteps);
    });
  }, [scopeList, filterSteps, lockedSteps, answeredQids]);

  const selectedCategories = useMemo(() => {
    const step = filterSteps.find((s) => s.key === "category");
    return step ? [...(step.values || [])] : [];
  }, [filterSteps]);

  const { scopeData, loading: summaryLoading, error: summaryError } = useScopeSummary(
    apiKey,
    selectedFY?.id,
    locationIds,
    selectedCategories,
  );
  const payload = scopeData?.[apiKey];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "var(--bg, #f1f5f9)" }}>
      <div
        style={{
          flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
          position: "sticky", top: 0, height: "100vh",
          background: "var(--surface)", borderRight: "1px solid var(--rule)",
          zIndex: 100, transition: "flex 0.2s ease",
        }}
      >
        <Sidebar dataFromParent={routePath} onSidebarToggle={setSidebarExpanded} />
      </div>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto", minWidth: 0 }}>
        <Header />

        <div style={{ padding: "16px 24px 40px" }}>
          {/* Header row: title + FY + Location + toggle */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, color: "var(--ink, #1e293b)", letterSpacing: "-0.02em" }}>{title}</h2>
              <span style={{ fontSize: 12, color: "var(--ink-4, #94a3b8)" }}>{filtered.length} items · {fyLabelOf(selectedFY)}</span>
            </div>
            <ViewToggle viewMode={viewMode} onChange={onViewChange} />
          </div>

          {/* Filters: FY + Location + Scope/Category tags */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <select
              className="kpi-inline-select"
              value={selectedFY?.id ?? ""}
              onChange={(e) => {
                const fy = financialYears.find((f) => String(f.id) === e.target.value);
                if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value, financial_year_value: fy.financial_year_value });
              }}
              title="Financial Year"
            >
              {financialYears.map((f) => <option key={f.id} value={f.id}>{f.financial_year_value || f.name}</option>)}
            </select>
            <select className="kpi-inline-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} title="Location">
              <option value="">All Locations</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <TagFilterBar allQuestions={tagQuestions} filterSteps={filterSteps} onFilterStepsChange={setFilterSteps} lockedSteps={lockedSteps} />
            </div>
            {payload && (
              <button type="button" style={downloadBtnStyle} onClick={() => downloadFyViewExcel({
                scopeTitle: title, fyLabel: fyLabelOf(selectedFY),
                locationLabel: locationId ? (locations.find((l) => String(l.id) === locationId)?.name || "1 location") : "All Locations",
                payload,
              })}>⬇ Download Excel</button>
            )}
          </div>

          {/* Dashboard */}
          <ScopeDashboard payload={payload} fyLabel={fyLabelOf(selectedFY)} loading={summaryLoading} error={summaryError} />

          {/* KPI + Emission list */}
          <div style={{ marginTop: 16, background: "#fff", border: "1px solid var(--rule, #e2e8f0)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", padding: "12px 20px", background: "#f8fafc", borderBottom: "1px solid #e5e8ed", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "#94a3b8", textTransform: "uppercase" }}>
              <span style={{ flex: 1 }}>KPI</span>
              <span style={{ width: 220, textAlign: "right" }}>Emission (FY)</span>
              <span style={{ width: 36 }} />
            </div>

            {isLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No KPIs with emissions for this scope.</div>
            ) : (
              filtered.map((item, i) => (
                <ScopeKpiRow
                  key={item.id}
                  item={item}
                  index={i}
                  expanded={expandedId === item.id}
                  onToggle={() => setExpandedId((prev) => (prev === item.id ? null : item.id))}
                />
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

/**
 * One KPI row: name + FY emission, expanding to the shared Data-Management
 * overview (emission/energy stat cards + YoY comparison), reused by import.
 * Action buttons are suppressed for the read-only carbon-footprint view via
 * `hasPermission={() => false}`.
 */
const ScopeKpiRow = ({ item, index, expanded, onToggle }) => {
  const { analytics, kpiTypes, matchingTriggers, triggersFallback, projection } = useKpiRowData(item);
  const val = item.fy_emission != null && item.fy_emission !== "-"
    ? `${formatNumber(item.fy_emission)}${item.emission_unit ? ` ${item.emission_unit}` : ""}`
    : "0";

  return (
    <div style={{ borderBottom: "1px solid #eef1f5" }}>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        style={{ display: "flex", alignItems: "center", padding: "16px 20px", cursor: "pointer", background: expanded ? "#eef4ff" : index % 2 ? "#f8fafd" : "#fff" }}
      >
        <span style={{ width: 28, color: "#94a3b8", fontSize: 13 }}>{index + 1}.</span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{item.kpi_name}</span>
        <span style={{ width: 220, textAlign: "right", fontSize: 13, color: "#334155", fontVariantNumeric: "tabular-nums" }}>{val}</span>
        <span
          aria-hidden="true"
          style={{ width: 36, textAlign: "center", color: "#94a3b8", fontSize: 12, transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "none" }}
        >
          ▾
        </span>
      </div>
      {expanded && (
        <div className="kpi-slide-down">
          <KpiRowOverview
            item={item}
            analytics={analytics}
            kpiTypes={kpiTypes}
            matchingTriggers={matchingTriggers}
            triggersFallback={triggersFallback}
            projection={projection}
            emissionMode
            hasPermission={() => false}
            canAddAndEditRecords={false}
            subQuestions={[]}
            onViewSubQuestions={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default ScopeListPage;
